import {
  AppShell as MantineAppShell,
  Burger,
  Group,
  Text,
  Menu,
  Avatar,
  UnstyledButton,
  Divider,
  Drawer,
  ScrollArea,
  Box,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Outlet, useNavigate } from "react-router-dom";
import { IconLogout, IconUser, IconChevronDown } from "@tabler/icons-react";
import { Brand } from "../branding/Brand";
import { TopNav, MobileNav } from "./TopNav";
import { useAuth } from "../auth/AuthContext";

function initials(name) {
  if (!name) return "?";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");
}

export function AppShell() {
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] =
    useDisclosure();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <MantineAppShell header={{ height: 64 }} padding="md">
      <MantineAppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          {/* Left: brand + (mobile) burger */}
          <Group gap="md" wrap="nowrap">
            <Burger
              opened={drawerOpened}
              onClick={toggleDrawer}
              size="sm"
              hiddenFrom="md"
              aria-label="Open navigation"
            />
            <UnstyledButton onClick={() => navigate("/")} aria-label="Home">
              <Brand variant="compact" />
            </UnstyledButton>
          </Group>

          {/* Middle: horizontal nav (desktop) */}
          <Box visibleFrom="md" style={{ flex: 1, marginLeft: 24 }}>
            <ScrollArea
              type="never"
              offsetScrollbars={false}
              scrollbarSize={0}
            >
              <TopNav />
            </ScrollArea>
          </Box>

          {/* Right: user menu */}
          <Menu shadow="md" width={240} position="bottom-end">
            <Menu.Target>
              <UnstyledButton>
                <Group gap="xs" wrap="nowrap">
                  <Avatar color="indigo" radius="xl" size={32}>
                    {initials(user?.fullName)}
                  </Avatar>
                  <Box visibleFrom="sm" style={{ lineHeight: 1.15 }}>
                    <Text size="sm" fw={500}>
                      {user?.fullName}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {user?.email}
                    </Text>
                  </Box>
                  <IconChevronDown size={14} />
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>
                Signed in as {user?.email}
                {user?.isAdmin && (
                  <Text component="span" size="xs" c="indigo.6" ml="xs">
                    admin
                  </Text>
                )}
              </Menu.Label>
              <Menu.Item leftSection={<IconUser size={14} />} disabled>
                Profile
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                color="red"
                leftSection={<IconLogout size={14} />}
                onClick={() => {
                  logout();
                  navigate("/login", { replace: true });
                }}
              >
                Log out
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
        <Divider />
      </MantineAppShell.Header>

      <MantineAppShell.Main>
        <Outlet />
      </MantineAppShell.Main>

      {/* Mobile drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        size="xs"
        padding={0}
        title={<Brand variant="compact" />}
        hiddenFrom="md"
      >
        <MobileNav onNavigate={closeDrawer} />
      </Drawer>
    </MantineAppShell>
  );
}
